/**
 * The application.
 *
 * The canvas is mounted first and the deck built into it on the next settle;
 * the glass chrome only appears once the deck is ready, so the boot screen is
 * never covered by a half-wired interface.
 */

import { useQueryClient } from '@tanstack/solid-query';
import { type Component, onSettled, Show } from 'solid-js';
import { boot, bootFailed } from '../app/boot.ts';
import { wireLibraryCache } from '../app/queries.ts';
import { booting } from '../state/ui.ts';
import styles from './App.module.css';
import { BootScreen } from './boot/BootScreen.tsx';
import { Hud } from './hud/Hud.tsx';
import { Modal } from './modal/Modal.tsx';
import { Sheet } from './sheet/Sheet.tsx';

export const App: Component = () => {
  let canvas!: HTMLCanvasElement;
  const queryClient = useQueryClient();

  /* Signing in or out changes whose answers are cached; a failure is worth
     saying out loud wherever it was hit. Both are wired here, once. */
  onSettled(() => wireLibraryCache(queryClient));

  onSettled(() => {
    let dispose: (() => void) | null = null;
    let cancelled = false;

    void boot(canvas)
      .then((result) => {
        if (cancelled) result.dispose();
        else dispose = result.dispose;
      })
      .catch(bootFailed);

    return () => {
      cancelled = true;
      dispose?.();
    };
  });

  return (
    <>
      <canvas id="stage" ref={canvas} class={styles.stage} aria-label="3D turntable" role="img" />
      <Show when={!booting()}>
        <Hud />
        <Sheet />
        <Modal />
      </Show>
      <BootScreen />
    </>
  );
};
